import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  alpha,
  useTheme,
  Divider,
  TextField,
} from "@mui/material";
import { Payments, ArrowForward } from "@mui/icons-material";
import DialogHeader from "./DialogHeader";
import NumberPad from "./NumberPad";

/**
 * CashPaymentDialog Component
 * Calculator interface for cash payments with change calculation
 */
const CashPaymentDialog = ({ open, onClose, onConfirm, totalAmount }) => {
  const theme = useTheme();
  const [cashReceived, setCashReceived] = useState("");
  const cashInputRef = useRef(null);

  const focusCashInput = useCallback(() => {
    const input = cashInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  useEffect(() => {
    if (!open) return;
    setCashReceived("");
    const timer = window.setTimeout(focusCashInput, 50);
    return () => window.clearTimeout(timer);
  }, [open, focusCashInput]);

  const changeAmount = cashReceived
    ? Math.max(0, parseFloat(cashReceived) - totalAmount)
    : 0;
  const isValid = cashReceived && parseFloat(cashReceived) >= totalAmount;

  const handleNumberClick = (num) => {
    setCashReceived((prev) => prev + num);
    window.setTimeout(focusCashInput, 0);
  };

  const handleClear = () => {
    setCashReceived("");
    window.setTimeout(focusCashInput, 0);
  };

  const handleBackspace = () => {
    setCashReceived((prev) => prev.slice(0, -1));
    window.setTimeout(focusCashInput, 0);
  };

  const handleDecimal = () => {
    if (!cashReceived.includes(".")) {
      setCashReceived((prev) => (prev || "0") + ".");
      window.setTimeout(focusCashInput, 0);
    }
  };

  // Unified handler for NumberPad component
  const handleNumPadClick = (btn) => {
    if (btn === "⌫") {
      handleBackspace();
    } else if (btn === ".") {
      handleDecimal();
    } else {
      handleNumberClick(btn);
    }
  };

  const handleQuickAmount = (amount) => {
    setCashReceived(amount.toString());
    window.setTimeout(focusCashInput, 0);
  };

  const handleCashChange = (event) => {
    const value = event.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setCashReceived(value);
    }
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm({ cashReceived: parseFloat(cashReceived), changeAmount });
    }
  };

  // Quick amount suggestions based on total
  const quickAmounts = [
    Math.ceil(totalAmount / 100) * 100, // Round up to nearest 100
    Math.ceil(totalAmount / 500) * 500, // Round up to nearest 500
    Math.ceil(totalAmount / 1000) * 1000, // Round up to nearest 1000
    Math.ceil(totalAmount / 5000) * 5000, // Round up to nearest 5000
  ].filter((val, idx, arr) => arr.indexOf(val) === idx && val <= 100000); // Remove duplicates and cap at 100k

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      TransitionProps={{
        onEntered: focusCashInput,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: alpha(theme.palette.grey[50], 0.98),
        },
      }}
    >
      {/* Header */}
      <DialogHeader
        icon={<Payments sx={{ fontSize: 24 }} />}
        title="Cash Payment"
        subtitle="Enter amount received from customer"
        onClose={onClose}
      />

      <Box sx={{ p: 3 }}>
        {/* Two Column Layout */}
        <Grid container spacing={3}>
          {/* Left Column - Amount Display */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Amount Display */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.7rem",
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 3,
                    fontFamily: "monospace",
                    fontSize: "2.5rem",
                  }}
                >
                  Rs. {totalAmount.toFixed(2)}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.7rem",
                  }}
                >
                  Cash Received
                </Typography>
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "white",
                    borderRadius: 1.5,
                    px: 2,
                    mt: 1,
                    mb: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:focus-within": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <TextField
                    inputRef={cashInputRef}
                    value={cashReceived}
                    onChange={handleCashChange}
                    placeholder="0.00"
                    variant="standard"
                    fullWidth
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    inputProps={{
                      inputMode: "decimal",
                      "aria-label": "Cash received",
                      style: {
                        textAlign: "center",
                        fontWeight: 800,
                        fontFamily: "monospace",
                        fontSize: "3rem",
                      },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        color: cashReceived
                          ? theme.palette.text.primary
                          : alpha(theme.palette.text.primary, 0.45),
                        "& input::placeholder": {
                          color: alpha(theme.palette.text.primary, 0.3),
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {cashReceived && parseFloat(cashReceived) >= totalAmount && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.dark,
                          mb: 1,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: 0.5,
                        }}
                      >
                        Change to Return
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.main,
                          fontFamily: "monospace",
                          fontSize: "2.5rem",
                        }}
                      >
                        Rs. {changeAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Quick Amounts */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 700,
                    mb: 1.5,
                    display: "block",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  Quick Select
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant="outlined"
                      onClick={() => handleQuickAmount(amount)}
                      sx={{
                        flex: 1,
                        minWidth: 90,
                        height: 48,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        borderRadius: 1.5,
                        borderWidth: 2,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        "&:hover": {
                          borderWidth: 2,
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      {amount}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleClear}
                  sx={{
                    flex: 1,
                    height: 100,
                    fontSize: "1rem",
                    fontWeight: 700,
                    borderRadius: 2,
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleConfirm}
                  disabled={!isValid}
                  endIcon={<ArrowForward />}
                  sx={{
                    flex: 2,
                    height: 100,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    borderRadius: 2,
                    background: isValid
                      ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                      : theme.palette.action.disabledBackground,
                    boxShadow: isValid
                      ? `0 4px 16px ${alpha(theme.palette.success.main, 0.4)}`
                      : "none",
                    "&:hover": {
                      boxShadow: isValid
                        ? `0 6px 20px ${alpha(theme.palette.success.main, 0.5)}`
                        : "none",
                      transform: isValid ? "translateY(-2px)" : "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Calculator */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[100], 0.5),
                border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: theme.palette.text.primary,
                }}
              >
                Number Pad
              </Typography>

              {/* Number Pad Component */}
              <NumberPad onNumberClick={handleNumPadClick} disabled={false} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default CashPaymentDialog;
